
export class BreachPredictionService {
  constructor() {
    this.weights = {
      trendSlope: 0.35,
      headroomRatio: 0.30,
      volatility: 0.20,
      historicalBreaches: 0.15,
    };
  }

  /**
   * Calculate trend slope from historical values
   */
  calculateTrendSlope(history) {
    if (!history || history.length < 2) return 0;

    const n = history.length;
    const values = history.map((h, i) => ({ x: i, y: parseFloat(h.value) || 0 }));

    const sumX = values.reduce((sum, v) => sum + v.x, 0);
    const sumY = values.reduce((sum, v) => sum + v.y, 0);
    const sumXY = values.reduce((sum, v) => sum + v.x * v.y, 0);
    const sumX2 = values.reduce((sum, v) => sum + v.x * v.x, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    return isNaN(slope) ? 0 : slope;
  }

  /**
   * Calculate volatility (standard deviation of changes)
   */
  calculateVolatility(history) {
    if (!history || history.length < 3) return 0;

    const values = history.map(h => parseFloat(h.value) || 0);
    const changes = [];

    for (let i = 1; i < values.length; i++) {
      changes.push(values[i] - values[i - 1]);
    }

    const mean = changes.reduce((sum, c) => sum + c, 0) / changes.length;
    const variance = changes.reduce((sum, c) => sum + Math.pow(c - mean, 2), 0) / changes.length;

    return Math.sqrt(variance);
  }

  /**
   * Calculate headroom ratio (current vs threshold)
   */
  calculateHeadroomRatio(currentValue, threshold, operator) {
    const current = parseFloat(currentValue) || 0;
    const thresh = parseFloat(threshold) || 1;

    if (operator === '<=' || operator === '<') {
      return current / thresh;
    } else if (operator === '>=' || operator === '>') {
      return thresh / (current || 0.001);
    }
    return Math.abs(current - thresh) / thresh;
  }

  /**
   * Predict breach probability for a single covenant
   */
  predictBreachProbability(covenant, history = []) {
    const { current_value, threshold, operator, status } = covenant;

    // Already in breach
    if (status === 'breach') {
      return { probability: 1.0, risk: 'critical', confidence: 0.95 };
    }

    // Calculate features
    const trendSlope = this.calculateTrendSlope(history);
    const volatility = this.calculateVolatility(history);
    const headroomRatio = this.calculateHeadroomRatio(current_value, threshold, operator);

    // Normalize features
    const normalizedTrend = this.normalizeTrend(trendSlope, operator);
    const normalizedHeadroom = Math.min(headroomRatio, 1.5) / 1.5;
    const normalizedVolatility = Math.min(volatility / (parseFloat(threshold) || 1), 1);
    const historicalBreachScore = status === 'at_risk' ? 0.7 : 0;

    // Calculate weighted score
    const score =
      this.weights.trendSlope * normalizedTrend +
      this.weights.headroomRatio * normalizedHeadroom +
      this.weights.volatility * normalizedVolatility +
      this.weights.historicalBreaches * historicalBreachScore;

    // Sigmoid transformation for probability
    const probability = 1 / (1 + Math.exp(-5 * (score - 0.5)));

    // Confidence based on data quality
    const confidence = Math.min(0.5 + (history.length * 0.1), 0.95);

    return {
      probability: Math.round(probability * 100) / 100,
      risk: this.getRiskLevel(probability),
      confidence: Math.round(confidence * 100) / 100,
      factors: {
        trend: normalizedTrend,
        headroom: normalizedHeadroom,
        volatility: normalizedVolatility,
        historical: historicalBreachScore,
      },
    };
  }

  /**
   * Normalize trend based on operator
   */
  normalizeTrend(slope, operator) {
    if (operator === '<=' || operator === '<') {
      return slope > 0 ? Math.min(slope * 10, 1) : 0;
    } else if (operator === '>=' || operator === '>') {
      return slope < 0 ? Math.min(Math.abs(slope) * 10, 1) : 0;
    }
    return Math.min(Math.abs(slope) * 10, 1);
  }

  /**
   * Get risk level from probability
   */
  getRiskLevel(probability) {
    if (probability >= 0.7) return 'critical';
    if (probability >= 0.4) return 'high';
    if (probability >= 0.2) return 'medium';
    return 'low';
  }

  /**
   * Predict time to breach (in periods)
   */
  predictTimeToBreatch(covenant, history = []) {
    if (!history || history.length < 2) return null;

    const { current_value, threshold, operator } = covenant;
    const slope = this.calculateTrendSlope(history);

    if (slope === 0) return null;

    const current = parseFloat(current_value) || 0;
    const thresh = parseFloat(threshold) || 0;

    let periodsToBreatch;

    if (operator === '<=' || operator === '<') {
      if (slope <= 0) return null;
      periodsToBreatch = (thresh - current) / slope;
    } else if (operator === '>=' || operator === '>') {
      if (slope >= 0) return null;
      periodsToBreatch = (current - thresh) / Math.abs(slope);
    } else {
      return null;
    }

    if (periodsToBreatch <= 0 || periodsToBreatch > 12) return null;

    return Math.ceil(periodsToBreatch);
  }

  /**
   * Batch predict for multiple covenants
   */
  predictBatch(covenants, historyMap = {}) {
    return covenants.map(covenant => {
      const history = historyMap[covenant.id] || [];
      const prediction = this.predictBreachProbability(covenant, history);
      const timeToBreatch = this.predictTimeToBreatch(covenant, history);

      return {
        covenantId: covenant.id,
        covenantName: covenant.name,
        loanId: covenant.loan_id,
        ...prediction,
        timeToBreatch,
      };
    });
  }

  /**
   * Get high-risk covenants
   */
  getHighRiskCovenants(predictions, threshold = 0.4) {
    return predictions
      .filter(p => p.probability >= threshold)
      .sort((a, b) => b.probability - a.probability);
  }

  /**
   * Generate prediction summary
   */
  generateSummary(predictions) {
    const total = predictions.length;
    const critical = predictions.filter(p => p.risk === 'critical').length;
    const high = predictions.filter(p => p.risk === 'high').length;
    const medium = predictions.filter(p => p.risk === 'medium').length;
    const low = predictions.filter(p => p.risk === 'low').length;

    const avgProbability = predictions.reduce((sum, p) => sum + p.probability, 0) / total;

    return {
      total,
      critical,
      high,
      medium,
      low,
      averageProbability: Math.round(avgProbability * 100) / 100,
      portfolioRisk: this.getRiskLevel(avgProbability),
    };
  }
}

export const breachPredictionService = new BreachPredictionService();
export default breachPredictionService;